import React from 'react';

import {
  Column,
  // getPaginationRowModel,
  // PaginationState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  RowData,
  useReactTable,
  type Cell,
  type ColumnDef as ColumnDefOriginal,
  type Header,
  type SortingState,
  type Table,
} from '@tanstack/react-table';
import { useTranslation } from 'modules/i18n';
import { MdArrowDownward, MdArrowUpward } from 'react-icons/md';
import TableCell, { TableCellProps } from './TableCell';
import classNames from 'classnames';

export type ColumnDef<T, K = any> = ColumnDefOriginal<T, K> & {
  accessorKey?: string;
  cellType?: TableCellProps<T>['type'];
  align?: 'center' | 'left' | 'right';
  sortBy?: string[];
  href?: TableCellProps<T>['href'];
};

export interface PaginationProps {
  page?: number;
  limit?: number;
  count?: number;
  pageSizes?: number[];
  onChangePage?: (page: number) => any;
  onChangeLimit?: (page: number) => any;
}

export type TableProps<T> = {
  columns: ColumnDef<T>[];
  data: T[];
  className?: string;
  onChangeFilter?: (filter: any) => any;
  onChangeSort?: (newSort: any) => any;
  onRowClick?: (row: T) => any;
  getRowClassName?: (row: T) => string;
  filters?: any;
  sort?: string;
  loading?: boolean;
} & PaginationProps;

const Pagination = ({
  page = 1,
  limit = 10,
  count = 0,
  pageSizes = [10, 20, 30, 40, 50],
  onChangePage: onExternalChangePage,
  onChangeLimit: onExternalChangeLimit,
}: PaginationProps) => {
  const { t } = useTranslation();

  const nbPages = Math.ceil((count || 0) / limit) || 1;
  const isSkipPagination = count === undefined;

  const canPreviousPage = page - 1 > 0;
  const canNextPage = isSkipPagination ? true : page + 1 <= nbPages;

  const onChangeLimit = (newLimit: number) => {
    onExternalChangeLimit && onExternalChangeLimit(newLimit);
  };

  const onChangePage = (newPage: number) => {
    onExternalChangePage && onExternalChangePage(newPage);
  };

  return (
    <>
      <nav className="pagination mt-4">
        <div>
          <button onClick={() => onChangePage(1)} disabled={!canPreviousPage}>
            {'<<'}
          </button>
          <button onClick={() => onChangePage(page - 1)} disabled={!canPreviousPage}>
            {'<'}
          </button>
          <span className="flex items-center gap-2">
            <span>{t('common.table:pagination.page')}</span>
            <strong>{t('common.table:pagination.page.on.nbPages', { page, nbPages })}</strong>
          </span>
          <small>{t('common.table:pagination.nbRows', { count })}</small>
          <button onClick={() => onChangePage(page + 1)} disabled={!canNextPage}>
            {'>'}
          </button>
          <button onClick={() => onChangePage(nbPages)} disabled={!canNextPage}>
            {'>>'}
          </button>
        </div>
        {nbPages > 1 && (
          <span>
            {t('common.table:pagination.page.goto')}
            <input
              type="number"
              defaultValue={Math.ceil(page / 2)}
              onChange={(e) => {
                onChangePage(e.target.value ? Number(e.target.value) : 0);
              }}
            />
          </span>
        )}
        <select
          value={limit}
          onChange={(e) => {
            onChangeLimit(Number(e.target.value));
          }}
        >
          {Array.from(new Set([limit, ...pageSizes]))
            .sort((a, b) => a - b)
            .map((pageSize) => (
              <option key={`pagesize_${pageSize}`} value={pageSize}>
                {t('common.table:pagination.limit.show', { pageSize })}
              </option>
            ))}
        </select>
      </nav>
    </>
  );
};

const Table = <T extends RowData>({
  columns,
  data,
  page = 1,
  limit = 10,
  sort,
  count,
  pageSizes,
  loading,
  onChangePage,
  onChangeLimit,
  onChangeFilter, // TODO
  onRowClick,
  onChangeSort,
  className = '',
  getRowClassName,
}: TableProps<T>) => {
  let defaultSort: SortingState = [];
  const sortArray = sort
    ? decodeURIComponent(sort)
        ?.replace(/(\-|\+)/gim, '')
        .split(' ')
    : [];
  const sortDirectionDesc = sort?.includes('-') || false;
  columns.forEach((column) => {
    if (column.accessorKey && JSON.stringify(column.sortBy || [column.accessorKey]) === JSON.stringify(sortArray)) {
      defaultSort = [{ id: column.accessorKey, desc: sortDirectionDesc }];
    }
  });
  const [sorting, setSorting] = React.useState<SortingState>(defaultSort);
  const [sortingColumnData, setSortingColumnData] = React.useState<ColumnDef<T> | undefined>(undefined);

  const table = useReactTable({
    data,
    state: {
      sorting,
    },
    manualSorting: true,
    onSortingChange: setSorting,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    debugTable: true,
  });

  const paginationEnabled = !!onChangePage;

  const cellRender = (cell: Cell<T, unknown>) => {
    return (
      <TableCell<T>
        type={(cell.column.columnDef as ColumnDef<T>).cellType}
        value={cell.getValue()}
        default={flexRender(cell.column.columnDef.cell, cell.getContext())}
        data={cell.row.original}
        {...(cell.column.columnDef as ColumnDef<T>)}
      />
    );
  };

  const cellAlign = (cell: Cell<T, unknown> | Header<T, unknown>) => {
    const align = (cell.column.columnDef as ColumnDef<T>).align;
    if (!align) {
      return '';
    }
    if (align === 'left') {
      return 'text-left justify-start';
    }
    if (align === 'right') {
      return 'text-right justify-end';
    }
    if (align === 'center') {
      return 'text-center justify-center';
    }
  };

  React.useEffect(() => {
    if (onChangeSort && sorting && sortingColumnData) {
      const sortFields = sortingColumnData?.sortBy || [sortingColumnData?.accessorKey];
      const direction = sorting.length === 0 ? 'reset' : sorting[0].desc ? 'desc' : 'asc';

      setSortingColumnData(undefined);
      if (direction === 'reset') {
        onChangeSort(undefined);
      } else {
        const mongoFilterString = sortFields.map((sortField) => `${direction === 'desc' ? '-' : ''}${sortField}`).join(' ');
        onChangeSort(encodeURIComponent(mongoFilterString));
      }
    }
  }, [onChangeSort, sorting, sortingColumnData]);

  return (
    <>
      <table className={className || 'simple'}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan} className={cellAlign(header)}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...(onChangeSort
                          ? {
                              className: header.column.getCanSort() ? `flex gap-1 items-center cursor-pointer select-none ${cellAlign(header)}` : '',
                              onClick: (...e) => {
                                setSortingColumnData(header.column.columnDef as ColumnDef<T>);
                                // @ts-ignore
                                return header.column.getToggleSortingHandler()(...e);
                              },
                            }
                          : {})}
                      >
                        <div>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {onChangeFilter && header.column.getCanFilter() ? <Filter column={header.column} table={table} /> : null}
                        </div>
                        <div>
                          {header.column.getCanSort()
                            ? {
                                asc: (
                                  <>
                                    {' '}
                                    <MdArrowUpward />
                                  </>
                                ),
                                desc: (
                                  <>
                                    {' '}
                                    <MdArrowDownward />
                                  </>
                                ),
                              }[header.column.getIsSorted() as string] ?? <span className="block size-3 "></span>
                            : null}
                        </div>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading &&
            [1, 2, 3, 4, 5].map((value) => (
              <tr key={`loading_${value}`}>
                <td colSpan={columns.length}>
                  <div role="status" className="size-full animate-pulse text-center">
                    <div className="mx-auto my-2 h-3.5 rounded-full bg-gray-200"></div>
                  </div>
                </td>
              </tr>
            ))}
          {table.getRowModel().rows.map((row) => {
            return (
              <tr
                key={row.id}
                {...(onRowClick
                  ? {
                      onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return onRowClick(row.original);
                      },
                    }
                  : {})}
                className={classNames(onRowClick ? 'hover cursor-pointer' : '', getRowClassName?.(row.original))}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td key={cell.id} className={cellAlign(cell)}>
                      {cellRender(cell)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {paginationEnabled && (
        <Pagination page={page} limit={limit} count={count} pageSizes={pageSizes} onChangePage={onChangePage} onChangeLimit={onChangeLimit} />
      )}
    </>
  );
};

function Filter({ column, table }: { column: Column<any, any>; table: Table<any> }) {
  const { t } = useTranslation();
  const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  return typeof firstValue === 'number' ? (
    <div className="filters">
      <input
        type="number"
        value={(columnFilterValue as [number, number])?.[0] ?? ''}
        onChange={(e) => column.setFilterValue((old: [number, number]) => [e.target.value, old?.[1]])}
        placeholder={t('common.table:filters.min')}
      />
      <input
        type="number"
        value={(columnFilterValue as [number, number])?.[1] ?? ''}
        onChange={(e) => column.setFilterValue((old: [number, number]) => [old?.[0], e.target.value])}
        placeholder={t('common.table:filters.max')}
      />
    </div>
  ) : (
    <div className="filters">
      <input
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={(e) => column.setFilterValue(e.target.value)}
        placeholder={t('common.table:filters.search')}
      />
    </div>
  );
}

export default Table;
